// ═══════════════════════════════════════════════════════════════
// XYQAR — WizardShell (auth + persona routing + step rendering)
// ═══════════════════════════════════════════════════════════════
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, ArrowRight, Shield, Lock, Check } from 'lucide-react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import {
  Persona, STEPS_BY_PERSONA, nextStep, prevStep,
  CLIENT_STEPS, TALENT_STEPS, PARTNER_STEPS,
} from './steps';
import { getFields, FieldInput } from './fields';

const logo = '/logo.png';

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE ?? 'https://api.xyqar.com';
const EXEC_URL = (import.meta as any).env?.VITE_EXEC_URL ?? 'https://clients.xyqar.com';
const EXPERT_URL = (import.meta as any).env?.VITE_EXPERT_URL ?? 'https://talent.xyqar.com';
const PARTNERS_URL = (import.meta as any).env?.VITE_PARTNERS_URL ?? 'https://partners.xyqar.com';
const GENESIS_URL = (import.meta as any).env?.VITE_GENESIS_URL ?? 'https://genesis.xyqar.com';
const ENGAGE_URL = (import.meta as any).env?.VITE_ENGAGE_URL ?? 'https://engage.xyqar.com';

function buildGenesisReturnUrl() {
  const url = new URL(GENESIS_URL);
  url.searchParams.set('return_to', window.location.href);
  return url.toString();
}

// In-memory wizard state (per session). Real submission uses POST to backend.
const SESSION_KEY = 'xyqar.onboarding.v1';
function loadSession(): Record<string, any> {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}'); } catch { return {}; }
}
function saveSession(d: Record<string, any>) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(d));
}

const TOKEN_WAIT_MS = 2000;

async function resolveAuthToken(getToken: (() => Promise<string>) | undefined): Promise<string> {
  if (!getToken) return '';
  try {
    return await Promise.race<string>([
      getToken(),
      new Promise<string>((resolve) => {
        window.setTimeout(() => resolve(''), TOKEN_WAIT_MS);
      }),
    ]);
  } catch {
    return '';
  }
}

interface MatchedCandidate {
  rank: number;
  blind_id: string;
  match_score: number;
  fairness_grade?: string;
  title?: string;
  seniority?: string;
  skills_top5?: string[];
  city_zone?: string;
  available_from?: string;
  wolf_tier?: string;
}

interface MissionMatchPayload {
  mission_id: string;
  count: number;
  candidates: MatchedCandidate[];
}

function clientMissionTitle(data: Record<string, any>): string {
  const role = String(data['client/identity/role'] ?? '').trim();
  const company = String(data['client/company/legal_name'] ?? '').trim();
  if (role && company) return `${role} for ${company}`;
  if (role) return role;
  return 'Cyber mission';
}

function clientMissionSkills(data: Record<string, any>): string[] {
  const stack = data['client/tech/stack'];
  return Array.isArray(stack) ? stack.filter((value) => typeof value === 'string' && value) : [];
}

function clientMissionDescription(data: Record<string, any>): string {
  const context = String(data['client/intake/context'] ?? '').trim();
  const notes = String(data['client/upload/notes'] ?? '').trim();
  const mode = String(data['client/intake/mode'] ?? '').trim();
  return [context, notes, mode ? `Mode: ${mode}` : ''].filter(Boolean).join('\n\n');
}

function clientMissionCityZone(data: Record<string, any>): string {
  const country = String(data['client/timeline/country'] ?? 'eu').toUpperCase();
  const location = String(data['client/timeline/location'] ?? 'remote');
  return `${country}/${location}`;
}

function clientMissionTjmBand(data: Record<string, any>): string {
  const mode = String(data['client/intake/mode'] ?? 'on_demand');
  if (mode === 'emergency') return '900-1100';
  if (mode === 'pool') return '700-900';
  return '500-700';
}

function clientMissionDurationDays(data: Record<string, any>): number {
  const months = Number(data['client/timeline/duration'] ?? 3);
  return Math.max(30, Math.min(730, months * 30));
}

function clientMissionPayload(data: Record<string, any>) {
  const title = clientMissionTitle(data);
  return {
    title,
    description: clientMissionDescription(data),
    skills: clientMissionSkills(data),
    seniority: 'senior',
    city_zone: clientMissionCityZone(data),
    tjm_band: clientMissionTjmBand(data),
    duration_days: clientMissionDurationDays(data),
  };
}

interface Props {
  persona?: Persona;
  step?:    string;
}

export default function WizardShell({ persona, step }: Props) {
  const { principal, loading, getToken } = useXyqarAuth();
  const navigate = useNavigate();
  const [data, setData] = React.useState<Record<string, any>>(() => loadSession());
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [completionNotice, setCompletionNotice] = React.useState<string | null>(null);

  // ── Auth gate (silent SSO) ────────────────────────────
  React.useEffect(() => {
    if (!loading && !principal) {
      window.location.replace(buildGenesisReturnUrl());
    }
  }, [loading, principal]);

  // ── If no persona specified, redirect to engage (role selection hub) ─
  React.useEffect(() => {
    if (loading || !principal) return;
    if (!persona) {
      window.location.replace(ENGAGE_URL);
    }
  }, [loading, principal, persona]);

  if (loading || !principal || !persona || !step) {
    return <LoadingShell />;
  }

  const fields = getFields(persona, step);
  const stepDef = STEPS_BY_PERSONA[persona].find((s) => s.slug === step)!;
  const progress = (stepDef.num / stepDef.total) * 100;
  const filledFields = fields.filter((field) => {
    const value = data[`${persona}/${step}/${field.name}`];
    return value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0);
  }).length;
  const completionTarget = persona === 'client'
    ? 'exec contract drafting'
    : persona === 'talent'
      ? 'candidate assessment portal'
      : 'partner cockpit';

  const update = (name: string, v: any) => {
    setData((d) => {
      const next = { ...d, [`${persona}/${step}/${name}`]: v };
      saveSession(next);
      return next;
    });
  };
  const valueFor = (name: string) => data[`${persona}/${step}/${name}`];

  const onPrev = () => {
    const p = prevStep(persona, step);
    if (p) navigate(`/${persona}/${p}`);
  };

  const onNext = async () => {
    setError(null);

    // Validate required fields
    for (const f of fields) {
      if (f.required) {
        const v = valueFor(f.name);
        if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
          setError(`Field "${f.label}" is required`);
          return;
        }
      }
    }

    // Persist this step server-side
    setSubmitting(true);
    try {
      const stepPayload: Record<string, any> = {};
      for (const f of fields) stepPayload[f.name] = valueFor(f.name);

      const token = await resolveAuthToken(getToken);
      const applicationResult = await fetch(`${API_BASE}/api/v1/onboarding/applications`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          wizard:  persona,
          email:   principal?.email,
          payload: {
            step,
            values: stepPayload,
          },
        }),
      }).catch(() => {/* non-blocking — frontend persists locally */});
      const applicationPayload = applicationResult && applicationResult.ok
        ? await applicationResult.json().catch(() => null)
        : null;
      const finalStep = !nextStep(persona, step);
      if (finalStep && persona === 'talent') {
        const cta = typeof applicationPayload?.cta_url === 'string' ? applicationPayload.cta_url : '';
        if (cta.includes('/m/')) {
          window.location.replace(cta);
          return;
        }
        setCompletionNotice('Assessment link pending.');
        return;
      }
    } finally {
      setSubmitting(false);
    }

    // Navigate to next step or complete
    const n = nextStep(persona, step);
    if (n) {
      navigate(`/${persona}/${n}`);
    } else {
      // Final step
      if (persona === 'client')  window.location.replace(`${EXEC_URL}/contracts/draft`);
      if (persona === 'partner') window.location.replace(PARTNERS_URL);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-x-hidden">
      <Header progress={progress} stepDef={stepDef} persona={persona} />

      <main className="pt-32 pb-32 px-6 max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${persona}-${step}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-8"
          >
            <div>
              <p className="text-[10px] tracking-[0.2em] text-white/40 uppercase font-mono">
                STEP {stepDef.num} / {stepDef.total} — {persona.toUpperCase()}
              </p>
              <h1 className="text-3xl sm:text-4xl font-normal text-white mt-2"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {stepDef.title}
              </h1>
            </div>

            {completionNotice && (
              <div className="rounded-md border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                {completionNotice}
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-md bg-white/5 border border-white/10 p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Step completion</p>
                <p className="mt-2 text-white text-sm">{filledFields}/{fields.length || 1} fields filled</p>
              </div>
              <div className="rounded-md bg-white/5 border border-white/10 p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Save behavior</p>
                <p className="mt-2 text-white text-sm">Local session persists immediately; backend save is attempted on continue.</p>
              </div>
              <div className="rounded-md bg-white/5 border border-white/10 p-4">
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Flow target</p>
                <p className="mt-2 text-white text-sm">Completion routes into {completionTarget}.</p>
              </div>
            </div>

            {/* Special "output" step — placeholder for blind candidate cards */}
            {persona === 'client' && step === 'output' ? (
              <BlindCandidates sessionData={data} getToken={getToken} update={update} onAccept={(wolfId) => {
                update('chosen_candidate', wolfId);
                onNext();
              }} />
            ) : persona === 'talent' && step === 'tests' ? (
              <TalentTests onComplete={onNext} />
            ) : persona === 'partner' && step === 'done' ? (
              <PartnerDone onContinue={onNext} />
            ) : (
              <div className="flex flex-col gap-6">
                {fields.map((f) => (
                  <div key={f.name} className="flex flex-col gap-2">
                    {f.kind !== 'checkbox' && (
                      <label className="text-xs uppercase tracking-wider text-white/60">
                        {f.label}
                        {f.required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                    )}
                    <FieldInput
                      field={f}
                      value={valueFor(f.name)}
                      onChange={(v) => update(f.name, v)}
                    />
                    {f.hint && <p className="text-[11px] text-white/40">{f.hint}</p>}
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <div className="rounded-md bg-white/[0.03] border border-white/10 px-4 py-4 text-sm text-white/65">
              Required fields are validated on continue. Optional fields can stay incomplete if they are not needed for the current lane.
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-white/10">
              <button
                type="button"
                onClick={onPrev}
                disabled={stepDef.num === 1}
                className="flex items-center gap-2 text-sm text-white/60
                  hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={submitting}
                className="flex items-center gap-2 bg-white text-black px-6 py-3
                  rounded-full text-xs font-bold tracking-[0.15em] uppercase
                  hover:opacity-90 transition disabled:opacity-50"
              >
                {submitting ? 'Saving…' : (
                  stepDef.num === stepDef.total ? 'Complete' : 'Continue'
                )}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </main>

      <Footer />
    </div>
  );
}

function Header({ progress, stepDef, persona }: { progress: number; stepDef: any; persona: string }) {
  return (
    <header className="fixed top-0 left-0 w-full z-50 px-6 pt-5 pb-4 bg-black/70 backdrop-blur-[20px] border-b border-white/5">
      <div className="flex items-center justify-between max-w-5xl mx-auto">
        <div className="flex items-center gap-4">
          <img src={logo} alt="XYQAR" className="h-6 w-auto" />
          <span className="text-[22px] font-bold tracking-[0.25em] text-white">XYQAR</span>
        </div>
        <span className="text-[10px] tracking-[0.2em] text-white/50 uppercase font-mono">
          {persona} · Step {stepDef.num}/{stepDef.total}
        </span>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/5">
        <motion.div
          className="h-full bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)]"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 px-8 py-4 bg-black/95 backdrop-blur-[40px]">
      <div className="flex items-center justify-between max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <Shield className="h-3.5 w-3.5" style={{ color: '#4AE290' }} />
          <span className="text-[10px] uppercase tracking-[0.08em] font-mono" style={{ color: '#4AE290' }}>
            [ E2E ENCRYPTED ]
          </span>
          <div className="h-4 w-px bg-white/15" />
          <Lock className="h-3.5 w-3.5 text-white/50" />
          <span className="text-[10px] uppercase tracking-[0.08em] font-mono text-white/50">
            Auth0 RS256 · GDPR
          </span>
        </div>
        <span className="text-[11px] text-white/40">© 2026 Bluepinksecurity INC</span>
      </div>
    </footer>
  );
}

function LoadingShell() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <motion.div
          className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
        />
        <p className="text-xs text-white/40 tracking-widest uppercase">Loading session…</p>
      </div>
    </div>
  );
}

// ─── Step 8 client: pre-matched blind candidates ──────────
function BlindCandidates({
  sessionData,
  getToken,
  update,
  onAccept,
}: {
  sessionData: Record<string, any>;
  getToken: () => Promise<string>;
  update: (name: string, value: any) => void;
  onAccept: (wolfId: string) => void;
}) {
  const [payload, setPayload] = React.useState<MissionMatchPayload | null>(null);
  const [busy, setBusy] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const mission = React.useMemo(() => clientMissionPayload(sessionData), [sessionData]);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setBusy(true);
      setError(null);
      try {
        const token = await resolveAuthToken(getToken);
        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        let missionId = String(sessionData['client/output/mission_id'] ?? '');
        if (!missionId) {
          const created = await fetch(`${API_BASE}/api/v1/client/missions`, {
            method: 'POST',
            headers,
            body: JSON.stringify(mission),
          });
          if (!created.ok) throw new Error(`mission create failed (${created.status})`);
          const createdJson = await created.json();
          missionId = String(createdJson.id ?? '');
          if (!missionId) throw new Error('mission id missing');
          update('mission_id', missionId);
          update('mission_title', mission.title);
        }

        const matched = await fetch(`${API_BASE}/api/v1/client/missions/${missionId}/match?top_n=3`, {
          method: 'POST',
          headers,
        });
        if (!matched.ok) throw new Error(`match failed (${matched.status})`);

        const detail = await fetch(`${API_BASE}/api/v1/client/missions/${missionId}/candidates`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!detail.ok) throw new Error(`candidates fetch failed (${detail.status})`);

        const detailJson = await detail.json() as MissionMatchPayload;
        if (cancelled) return;
        setPayload(detailJson);
        update('candidate_count', detailJson.count);
      } catch (e: any) {
        if (!cancelled) setError(String(e.message ?? e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [getToken, mission, sessionData, update]);

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md bg-white/5 border border-white/10 p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Live mandate</p>
        <p className="mt-2 text-lg font-bold text-white">{mission.title}</p>
        <p className="mt-2 text-sm text-white/60">{(mission.skills.length ? mission.skills.join(' · ') : 'Generalist cyber scope')} · {mission.city_zone} · {mission.tjm_band}</p>
      </div>
      {busy && (
        <div className="rounded-md bg-white/5 border border-white/10 p-4 text-sm text-white/60">
          Running Wolf Brain v3 on the live brief and loading the top 3 anonymised candidates…
        </div>
      )}
      {error && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 p-4 text-sm text-red-300">
          {error}
        </div>
      )}
      {!busy && !error && (payload?.candidates ?? []).length === 0 && (
        <div className="rounded-md bg-white/5 border border-white/10 p-4 text-sm text-white/60">
          No candidate surfaced for this brief yet.
        </div>
      )}
      {(payload?.candidates ?? []).map((candidate) => (
        <div key={candidate.blind_id} className="rounded-md bg-white/5 border border-white/10 p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">#{candidate.rank} · {candidate.blind_id}</p>
            <p className="text-lg font-bold text-white mt-1">Wolf Score {Math.round((candidate.match_score ?? 0) * 100)}%</p>
            <p className="text-xs text-white/40 mt-1">{candidate.title ?? 'Anonymised cyber profile'} · {candidate.seniority ?? 'senior'} · {candidate.wolf_tier ?? 'tier —'}</p>
            <div className="flex gap-2 mt-2">
              {(candidate.skills_top5 ?? []).slice(0, 3).map((s) => (
                <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 uppercase tracking-wider">{s}</span>
              ))}
            </div>
            <p className="text-xs text-white/40 mt-2">Availability: {candidate.available_from ?? 'now'} · {candidate.city_zone ?? 'EU/remote'} · fairness {candidate.fairness_grade ?? '—'}</p>
          </div>
          <div className="flex gap-2">
            <a
              href={`${API_BASE}/api/v1/client/blind/${candidate.blind_id}/fiche.html`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded-full bg-white/5 border border-white/20 hover:border-white/40 text-[10px] uppercase tracking-wider text-white/70 transition"
            >
              Fiche
            </a>
            <button
              type="button"
              onClick={() => onAccept(candidate.blind_id)}
              className="w-10 h-10 rounded-full bg-white text-black hover:bg-white/90 flex items-center justify-center transition"
              title="Meet this talent"
            ><Check className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Step 5 talent: 4 SaaS tests launcher ─────────────────
function TalentTests({ onComplete }: { onComplete: () => void }) {
  const dimensions = [
    { id: 'technical', label: 'Technical', est: '4 prompts' },
    { id: 'cognitive', label: 'Cognitive', est: '4 prompts' },
    { id: 'environment', label: 'Environment', est: '4 prompts' },
    { id: 'softskill', label: 'Soft skills', est: '4 prompts' },
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md bg-indigo-500/10 border border-indigo-400/30 p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-indigo-200/70 font-mono">Secure assessment link</p>
        <p className="mt-2 text-sm text-white/80">Locked until prequalification.</p>
      </div>
      {dimensions.map((t) => (
        <div key={t.id} className="rounded-md bg-white/5 border border-white/10 p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-white">{t.label}</p>
            <p className="text-xs text-white/40 mt-1">~ {t.est}</p>
          </div>
          <span className="px-4 py-2 rounded-full text-xs uppercase tracking-wider bg-white/10 text-white/70">
            Locked
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onComplete}
          className="bg-white text-black px-6 py-3 rounded-full text-xs font-bold tracking-[0.15em] uppercase hover:opacity-90"
        >
          Continue onboarding
        </button>
      </div>
    </div>
  );
}

function PartnerDone({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex flex-col items-center gap-6 py-12">
      <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
        <Check className="h-8 w-8 text-green-400" />
      </div>
      <div className="text-center">
        <h2 className="text-2xl text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Onboarding complete.
        </h2>
        <p className="text-sm text-white/60 max-w-md">
          Your partner profile is now provisioned. We'll email you a secure link to <code className="font-mono">partners.xyqar.com</code>.
        </p>
      </div>
      <button
        type="button"
        onClick={onContinue}
        className="bg-white text-black px-6 py-3 rounded-full text-xs font-bold tracking-[0.15em] uppercase"
      >
        Open partner portal
      </button>
    </div>
  );
}

// silence unused warnings
void CLIENT_STEPS; void TALENT_STEPS; void PARTNER_STEPS; void GENESIS_URL;
